import axios from 'axios';
import { Client } from '@lemonade-hq/nuiza';

const DEFAULT_RETRIES = 3;
const DEFAULT_INITIAL_DELAY_MS = 200;
const DEFAULT_TIMEOUT_MS = 5000;

type FetchWithRetryOptions = {
  retries?: number;
  initialDelayMs?: number;
  shouldRetry?: (error: unknown) => boolean;
  timeout?: number;
  headers?: Record<string, string>;
};

const isRetryableError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;

  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return true;

  const status = error.response?.status;
  if (status == null) return true;

  return status >= 500 || status === 408 || status === 429;
};

export async function fetchWithRetry<T>(url: string, options: FetchWithRetryOptions = {}): Promise<T> {
  const {
    retries = DEFAULT_RETRIES,
    initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
    shouldRetry = isRetryableError,
    timeout = DEFAULT_TIMEOUT_MS,
    headers,
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await Client.get<T>(url, { headers, timeout });
    } catch (error) {
      lastError = error;

      const isLastAttempt = attempt === retries;
      if (isLastAttempt || !shouldRetry(error)) {
        throw error;
      }

      const delayMs = initialDelayMs * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  throw lastError;
}
